/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadAssets} from '@ion/core/src/services/Connect';
import {Asset} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseAssetsDropdownParams {
  accountId: number;
  pageId: number;
  pageSize: number;
  searchTerms: string;
};

export type UseAssetsDropdownResult = {
  asset: Asset;
  loadAsset: (params: UseAssetsDropdownParams, callbacks) => void;
}

/**
 * The useAssetsDropdown hook is used to retrieve assets.
 *
 * ```typescript
 *
 *  import {useAssetsDropdown} from '@ion/packages/query/src/hooks';
 *
 *  const {asset} = useAssetsDropdown({
 *    accountId,
 *    pageId,
 *    pgSize,
 *    searchTerms
 *  });
 *
 * ```
 */
export const useAssetsDropdown = (): UseAssetsDropdownResult => {
  const mutation = useMutation(async(params: UseAssetsDropdownParams) => {
    const {
      accountId,
      pageId,
      pageSize,
      searchTerms
    } = params;
    const assets = await loadAssets({
      pageId   : pageId,
      pageSize : pageSize,
      sortOn   : ['+name'],
      filterOn : searchTerms?.length ? {
        name : `*${searchTerms?.toLowerCase()}*`
      } : null,
      accountId
    });
    if (assets) {
      return assets;
    }
    return '';
  });

  return createMutationResult(
    mutation, {
      loadAsset : mutation.mutate
    }
  ) as UseAssetsDropdownResult;
};

export default useAssetsDropdown;
